import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import PlaceIcon from '@mui/icons-material/Place'
import { useTranslation } from 'react-i18next'

import type { AttractionDto } from '../../types/api'

const CATEGORY_ICONS: Record<string, string> = {
  NATURE: '🌲',
  HISTORY: '🏰',
  CULTURE: '🎨',
  FOOD: '🥧',
  FAMILY: '👨‍👩‍👧',
  EVENT: '🎭',
  SHOPPING: '🛍️',
  WELLNESS: '🧘',
}

export function AttractionCard({
  attraction,
  showDestination,
}: {
  attraction: AttractionDto
  showDestination?: boolean
}) {
  const { t } = useTranslation()

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {attraction.imageUrl ? (
        <Box
          component="img"
          src={attraction.imageUrl}
          alt={attraction.title}
          loading="lazy"
          sx={{ width: '100%', aspectRatio: '16 / 10', objectFit: 'cover' }}
        />
      ) : (
        <Box
          sx={{
            aspectRatio: '16 / 10',
            display: 'grid',
            placeItems: 'center',
            fontSize: '2.5rem',
            backgroundColor: (theme) => theme.palette.action.hover,
          }}
          aria-hidden
        >
          {CATEGORY_ICONS[attraction.category] ?? '📍'}
        </Box>
      )}

      <CardContent sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
        <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
          <Chip
            size="small"
            label={`${CATEGORY_ICONS[attraction.category] ?? '📍'} ${attraction.category.toLowerCase()}`}
            variant="outlined"
          />
          {showDestination && (
            <Chip
              size="small"
              icon={<PlaceIcon sx={{ fontSize: 16 }} />}
              label={attraction.destinationName}
            />
          )}
        </Stack>

        <Typography variant="h4" component="h3">
          {attraction.title}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          {attraction.summary}
        </Typography>

        <Stack spacing={0.25} sx={{ pt: 0.5 }}>
          {attraction.openingHours && (
            <Typography variant="caption" color="text.secondary">
              🕐 {attraction.openingHours}
            </Typography>
          )}
          {attraction.priceInfo && (
            <Typography variant="caption" color="text.secondary">
              🎫 {attraction.priceInfo}
            </Typography>
          )}
        </Stack>

        {attraction.websiteUrl && (
          <Link
            href={attraction.websiteUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}
          >
            {t('explore.website')}
            <OpenInNewIcon sx={{ fontSize: 15 }} />
          </Link>
        )}
      </CardContent>
    </Card>
  )
}
